import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import {TablePagination, Button, Tooltip, IconButton, Icon, useTheme} from "@material-ui/core";
import PlayIcon from "@material-ui/icons/PlayCircleFilled";
import TrafficLight from "../../../controls/TrafficLight";

import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import generatePermitDialog from "./GeneratePermitDialog";

import {
  MaskedText,
} from "../../../controls";
import {
  useVRequiredPermits,

  useAddPermit
} from "../../../services/permits";
import {ColumnWidth} from "../../../blocks/ColumnHelper";

const apiSitePrefix = getApiSitePrefix();


const RequiredPermitList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const props = {
    ..._props
  }
  const classes = _props.classes;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});
  const handleAddPermit = useAddPermit();
  const queryClient = useQueryClient();
  const extraKeys = [];

  const requiredPermits = useVRequiredPermits({tableRef, queryClient, extraKeys});

  const formatChars={
    "U": "[A-Z]",
    "A": "[A-Z0-9]",
    "a": "[A-Za-z0-9]",
    "i": "[A-Za-z0-9\\-]",
    "9": "[0-9]",
    "D": "[0-9.]",
    "-": "-",
    "x": "."
  }

  const refreshList = async () => {
    queryClient.invalidateQueries(["VRequiredPermits"]);
  }

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [

      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        cellStyle: { padding: "8px 10px 8px 12px" },
        headerStyle: { padding: "8px 10px 8px 12px" },
        title: "Permit Type",
        field: "permitType",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedText
            mask={"i".repeat(36)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Country / Station",
        field: "countryOrAirport",
        emptyValue: null,
        initialEditValue: null,
        cellStyle: ((cell,rowData,updatedData) => {
          const color = rowData && rowData.country && rowData.country.overflightRestricted ? "red" : "";
          return ({backgroundColor: color });
        }),
        customFilterAndSearch: (term, rowData) => {
          const countryName =  rowData.country.countrySearchName; //shortenCountryName(rowData.countryName);
          const searchValue = `${countryName}${rowData.airportCode ? ' / ' + rowData.airportCode : ''}`.toUpperCase();
          return searchValue.indexOf(term.toUpperCase()) != -1
        },
        editComponent: (props) => (
          <MaskedText
            mask={"i".repeat(36)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData => {
          const countryName =  rowData.country.countrySearchName; //shortenCountryName(rowData.countryName);
            return (
              <Tooltip 
                title={rowData && rowData.country && rowData.country.overflightRestricted ? `Overflight is restricted for ${rowData.countryName}` : `${rowData.country.countrySearchName}`}
                placement="right-start"
              >
                <div>{`${countryName}${rowData.airportCode ? ' / ' + rowData.airportCode : ''}`}</div>
              </Tooltip>
            );
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140)),
        title: "Call Signs",
        field: "callSigns",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(2)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140)),
        title: "Aircraft",
        field: "tailNumbers",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => (
          <div style={{width: 140, maxWidth:140, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
              {rowData.tailNumbers}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(200)),
        title: "Route",
        field: "route",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period",
        field: "period",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(6)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData => {
          const period =  moment(rowData.period, "YYYYMM").format("MMM-YYYY");
          return (<div>{period}</div>);
        },
        customFilterAndSearch: (term, rowData) => {
          const period =  moment(rowData.period, "YYYYMM").format("MMM-YYYY").toUpperCase();
          return period.indexOf(term.toUpperCase()) !== -1
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130)),
        title: "Dates of Operation",
        field: "permitDates",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Day of Operation",
        field: "DOW",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Request Lead-Time Hours",
        field: "country.permitRequestLeadTimeHours",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(3)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Request By",
        field: "requestBy",
        initialEditValue: "",
        editable: "never",
        render: rowData => {
           return (<div>{moment.utc(rowData.requestBy).format('DD-MMM HH:mm')}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Validity Hours",
        field: "country.permitValidityHours",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(3)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "",
        field: "requestBy",
        initialEditValue: "",
        editable: "never",
        render: rowData => {
          const now = new Date();
          const diff = moment.utc(rowData.requestBy).diff(moment(now).utc(), 'days', false);
          return (
            <TrafficLight 
              height={20} 
              width={53} 
              lights={3} 
              status={diff > 2 ? "green" : diff <=0 ? "red": "amber"}
              backgroundStyle={{fill: "#f0f0f0"}}
              offStyle={{fill: "#e0e0e0"}}  
            />
          );
        },
      },
    ];
  };

  const {actionPermit, dialogBox} = generatePermitDialog(SystemData, apiSitePrefix, queryClient, extraKeys, handleAddPermit, formatChars);

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            pageSize: 15,
            detailPanelType: "single",
            padding: "dense",
          }}
          components={{
            ...defaultComponents,
            Pagination: (props) => (
                <TablePagination
                    {...props}
                    rowsPerPageOptions={[5, 10, 15]}
                />
            ),
            Action: _props => {
              let props = {..._props};
              if (typeof props.action.action === "function") {
                props.action = {..._props.action, ..._props.action.action(_props.data)};
              }

              if (props.action.icon==="generate") {
                return (
                  <Button style={{height: 24, marginTop: 6}}
                          variant="contained"
                          color="primary"
                          startIcon={<PlayIcon/>}
                          className={classes.button}
                          onClick={() => {
                            props.action.onClick(props.data)
                          }}
                  >
                    {props.action.text}
                  </Button>
                )
              } else if (props.action.icon) {
                if (props.action.tooltip) {
                  return (<Tooltip title={props.action.tooltip}><IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton></Tooltip>)
                }
                return (<IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton>)
              } else {
                return (<></>)
              }
            }
          }}
          title="New Permit Requirements"
          columns={getColumns({canEdit: state.canEdit})}
          data={requiredPermits.data}
          actions={
            [
              (rowData) => ({
                icon: "generate",
                text: "Generate",
                tooltip: "Convert required permit to a Permit Request",
                onClick: actionPermit,
                disabled: !state.canEdit
              }),
              {
                icon: 'refresh',
                tooltip: "Refresh List",
                isFreeAction: true,
                onClick: (e) => {
                  refreshList();
                }
              }
            ]
          }
        />

        {dialogBox}
      </div>
  );

}

export default RequiredPermitList;
